import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ExternalLink, BookOpen, Award, TrendingUp, Shield, Video } from 'lucide-react';
import { courses, products } from '../data/mock';
import SEO from '../components/SEO';

const Courses = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRO Exam Prep Courses 2025 - CIRE, RSE, ISE, Derivatives | Canada's #1 Provider"
        description="Browse all CIRO exam prep courses. Complete preparation for CIRE, RSE, ISE & Derivatives exams. Expert-led video lessons, AI tutor, 800+ practice questions, mock exams. Monthly plans from $29.99. Industry leading pass rate. IIROC & MFDA transition courses available."
        keywords="CIRO exam prep course, CIRO exam prep provider, CIRE exam prep, RSE exam prep, ISE exam prep, Derivatives exam prep, CIRO courses, securities course Canada, Canadian securities licensing course, investment advisor licensing Canada, Fitch Learning CIRO exam, Canadian Securities Institute CIRO, CSI CIRO courses, IIROC to CIRO transition, MFDA to CIRO transition, CIRO proficiency model changes 2026, CIRO exam courses Canada, CIRO exam courses Alberta, CIRO exam courses Ontario, CIRO exam courses Toronto"
        ogTitle="CIRO Exam Preparation Courses - All Certifications | Vitta Finance Academy"
        ogDescription="Browse all CIRO exam prep courses. Expert-led training for CIRE, RSE, ISE, and Derivatives. Industry leading pass rate. Trusted by 30,000+ Canadian finance professionals."
      />
      {/* Header Section */}
      <section style={{
        padding: '120px 0 80px',
        backgroundColor: 'var(--bg-card)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="brand-display" style={{ marginBottom: '24px' }}>
            CIRO Exam Prep Courses
          </h1>
          <p className="body-large" style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            color: 'var(--neutral-light)'
          }}>
            Complete preparation programs for CIRE, Retail Securities, and Institutional Securities exams
          </p>
          <Link to="/exam-comparison" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '24px',
            padding: '12px 24px',
            backgroundColor: 'var(--gold-primary)',
            color: 'white',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            transition: 'transform 0.2s ease'
          }}>
            Not sure which exam to take? Compare all CIRO exams <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Online Courses Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px' }}>
            Online Video Courses
          </h2>
          <p className="body-medium" style={{ marginBottom: '60px', color: 'var(--text-secondary)' }}>
            Instructor-led videos, podcasts, AI tutor, mock exams, and question banks
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {courses.map((course, idx) => (
              <div key={course.id} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '2px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                {/* Course Image */}
                <div style={{
                  height: '240px',
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: 'var(--neutral-light)'
                }}>
                  {course.status === 'coming-soon' && (
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '16px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      zIndex: 10
                    }}>COMING SOON</div>
                  )}
                  <img 
                    src={[
                      '/images/courses/course-1.jpg',
                      '/images/courses/course-2.jpg',
                      '/images/courses/course-3.jpg',
                      '/images/courses/course-4.jpg',
                      '/images/courses/traders-exam.jpg'
                    ][idx] || '/images/courses/default-course.jpg'}
                    alt={course.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{
                  padding: '40px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <h3 className="heading-3" style={{ 
                    marginBottom: '20px',
                    fontSize: 'clamp(1.5rem, 2vw, 2rem)'
                  }}>
                    {course.title}
                  </h3>
                  <p className="body-medium" style={{ 
                    marginBottom: '32px',
                    flex: 1
                  }}>
                    {course.description}
                  </p>
                  
                  <div style={{ marginBottom: '32px' }}>
                    <h4 className="heading-5" style={{ marginBottom: '16px' }}>What's Included:</h4>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0
                    }}>
                      {course.features.map((feature, idx) => (
                        <li key={idx} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <CheckCircle size={20} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span className="body-small">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div style={{
                    backgroundColor: 'var(--bg-card)',
                    padding: '24px',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    {course.priceType === 'one-time' ? (
                      <>
                        <p className="caption" style={{ marginBottom: '8px' }}>One-Time Payment</p>
                        <p style={{ 
                          fontSize: '2rem',
                          fontWeight: 900,
                          color: '#0d9488'
                        }}>
                          CAD ${course.price}
                        </p>
                        <p className="body-small">Lifetime Access</p>
                      </>
                    ) : (
                      <>
                        <p className="caption" style={{ marginBottom: '8px' }}>Monthly Subscription</p>
                        <p style={{ 
                          fontSize: '2rem',
                          fontWeight: 900,
                          color: 'var(--brand-primary)'
                        }}>
                          CAD ${course.monthlyPrice}
                          <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-secondary)' }}>/month</span>
                        </p>
                        <p className="body-small">Cancel anytime</p>
                      </>
                    )}
                  </div>
                  
                  {course.status === 'coming-soon' ? (
                    <Link to={course.link} className="btn-primary" style={{ width: '100%', backgroundColor: '#0d9488', borderColor: '#0d9488' }}>
                      Learn More <ArrowRight size={20} />
                    </Link>
                  ) : (
                    <a href={course.kajabLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ width: '100%' }}>
                      Enroll Now <ArrowRight size={20} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Exams Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>
            About the CIRO Exams
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {[
              {
                title: 'About CIRE Exam',
                description: 'The Canadian Investment Regulatory Exam (CIRE) by CIRO is the proficiency requirement for Registered Representative (RR) and Investment Representative (IR) registration. The exam features 110 multiple-choice questions in 2 hours and tests real-world application across Elements 1–9: regulatory framework, KYC/suitability, account scope, complaints, market & company analysis, market integrity/trade execution/settlement, securities & managed products (incl. mutual funds), derivatives, and conflicts & ethics.',
                link: '/cire-exam'
              },
              {
                title: 'About RSE Exam',
                description: 'The Retail Securities Exam (RSE) by CIRO is the core proficiency requirement for client-facing investment roles, including advisors who provide product recommendations and suitability assessments. The exam evaluates applied competency across KYC, KYP, client suitability, investment products, portfolio construction, taxation, compliance, and ethical decision-making. The RSE emphasizes real-world scenarios, requiring candidates to interpret client profiles, identify red flags, assess risk tolerance and capacity, compare products, and apply CFR-aligned recommendations.',
                link: '/rse-exam'
              },
              {
                title: 'About ISE Exam',
                description: 'The Institutional Securities Exam (ISE) by CIRO is designed for professionals working in institutional securities roles. The exam covers 100 multiple-choice questions in 2.5 hours and focuses on institutional trading, corporate finance, advanced product knowledge, and regulatory compliance specific to institutional clients. Candidates are tested on complex trading scenarios, market structure, and institutional-level risk management.',
                link: '/ise-exam'
              },
              {
                title: 'About Derivatives Exam',
                description: 'The Derivatives Exam by CIRO is the proficiency requirement for professionals dealing in options, futures, and other derivative instruments. The exam features 120 questions over 3 hours and covers options strategies, futures contracts, hedging techniques, pricing models, risk management, and regulatory requirements for derivatives trading. Candidates must demonstrate competency in both theoretical concepts and practical application of derivative strategies.',
                link: '/derivatives-exam'
              }
            ].map((exam, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '32px',
                borderRadius: '12px',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-4" style={{ 
                  marginBottom: '20px',
                  color: 'var(--brand-primary)'
                }}>
                  {exam.title}
                </h3>
                <p className="body-small" style={{ lineHeight: 1.7, marginBottom: '24px' }}>
                  {exam.description}
                </p>
                <Link to={exam.link} className="btn-secondary" style={{ display: 'inline-flex' }}>
                  Learn More <ArrowRight size={18} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;